/**
 * 队伍系统类型定义
 * 包含队伍状态、阵型等
 */

import { z } from 'zod';
import { CharacterSchema } from '@/IsekaiRPG/modules/character/characterEntityTypes';

// ============================================
// 队伍状态
// ============================================

/**
 * 队伍位置结构 - 层级式设计
 * 采用世界层 -> 区域层 -> 内部层嵌套逻辑
 */
export const PartyLocationSchema = z.object({
  // 1. 世界层级 (World Layer) - 【始终存在】
  // 负责在大地图上渲染队伍标签
  world: z
    .object({
      x: z.number().default(0), // 大地图经度/X坐标
      y: z.number().default(0), // 大地图纬度/Y坐标
      country: z.string().optional(), // 所在的国家信息
    })
    .default({ x: 0, y: 0 }),

  // 2. 区域层级 (Zone Layer) - 【可选】
  // 负责城镇界面的交互、城镇地图内的标签渲染。如果为空则表示在野外。
  zone: z
    .object({
      id: z.string(), // 城市/区域 ID (对应 WorldCity.id 或 WorldSite.id)
      // 新增类型区分：城市 vs 站点(地牢/遗迹) vs 村庄据点
      type: z.enum(['city', 'site', 'village']).default('city'),
      name: z.string(), // 显示名称
      localX: z.number().default(0), // 城镇地图内的 X 坐标
      localY: z.number().default(0), // 城镇地图内的 Y 坐标
    })
    .optional(),

  // 3. 室内/交互层级 (Interior Layer) - 【可选】
  // 负责进入建筑专属界面。如果为空则表示在城镇街道上。
  interior: z
    .object({
      buildingId: z.string(), // 建筑 ID (对应 MapBuilding.id)
      floor: z.number().default(1), // 楼层
    })
    .optional(),
});

export type PartyLocation = z.infer<typeof PartyLocationSchema>;

/**
 * 队伍状态
 * 定义当前的队伍构成和阵型
 */
export const PartyStateSchema = z.object({
  members: z.array(CharacterSchema).default([]),
  formation: z.array(z.string()).default([]), // 成员 ID 顺序
  maxSize: z.number().default(6),
  location: PartyLocationSchema.default({
    world: { x: 0, y: 0 },
  }), // 当前位置状态
  visitedLocations: z.array(z.string()).default([]), // 已访问位置
});

export type PartyState = z.infer<typeof PartyStateSchema>;
